import React, { useEffect, useRef, useState } from 'react';
import { useForm } from '../context/FormContext';
import SignaturePad from 'signature_pad';

const AUTH_TEXT = {
  section1: `You authorize us to use your electronic signature for the following DOT Driver Qualification File, Employment documents, attachments, addendums including, without limitation (collectively, the "Documents") in any way connected to the establishment of a USDOT Driver Qualification File as defined by Motor Carrier Solutions, Inc being entered into between you and Motor Carrier Solutions, Inc and their Client. This consent is specifically to permit an electronic signature in lieu of hand-written signatures on any one or more of the below listed Documents. You agree that your electronic signature will be enforceable as and to the full extent of a hand-written signature as an original for enforcement/enforceability of the Documents containing the electronic signature(s), whether in court (state or federal), arbitration or otherwise. You will not raise any defenses or invoke regulatory or statutory claims attempting to invalidate the enforceability of the Documents to which the electronic signature is affixed.`,
  documents: [
    'Certification for Application for Employment',
    'Consent for Limited Queries of the Federal Motor Carrier Safety Administration (FMCSA) Drug and Alcohol Clearinghouse',
    'Safety Performance History Records Request',
    'Release and Documentation of Testing Information by Previous Employer',
    'Release and Documentation of Pre-Employment Testing Information by Driver/Applicant',
    'Pre-Employment Drug Testing Notification and Consent',
    'Certificate of Compliance and Understanding of the Commercial Motor Vehicle Safety Act of 1986',
    'Drivers Statement of On-Duty Hours for Newly Hired Drivers and Certification of Understanding and Compliance with: Federal Motor Carrier Safety Regulations § 395.8(j)(2) and Federal Motor Carrier Safety Regulations § 395.2 (8) and (9)',
    'Certificate of Receipt for Employee Educational Materials',
    'Certificate of Release for Information Relating to Pre-Employment or Annual Physical Examinations',
    'Authorization of a Request for Check of Driving Record',
    'Certification of Annual Certificate of Violations and Review of Driving Record',
    'Authorization of Disclosure and Consent to Procurement of Consumer Report Under the Fair Credit Reporting Act',
    'Receipt, Reading and Acceptance of Company Drug and Alcohol Policy',
    'Authorization of Release and Disclosure of Past Employer DOT Drug & Alcohol Records per 49 CFR § 391.23',
    'Receipt, Reading and Acceptance of Company Driving and Cellular Phone Use Policy',
    'Form I-9 USCIS OMB No. 1615-0047 Employment Eligibility Verification (current version)',
    'W-4 IRS Employee\'s Withholding Certificate OMB No. 1545-0074',
    'Intermittent or Multi-employer Checklist Authorization (MCS v050923)',
  ],
  section3: `I Acknowledge that printed versions of these documents were offered and I was advised of the contents of each prior to my authorization below. I also accept that any failed disclosure through error or omission will not nullify this authorization as it pertains to the included documents enumerated here.\n\nI certify that facsimile or photographic copies of this authorization are as valid as the original.`,
};

export default function StepSignature({ onNext, onBack }) {
  const { state, dispatch } = useForm();
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [errs, setErrs] = useState({});
  const [hasSig, setHasSig] = useState(!!state.signature.signatureData);
  const company = state.companyOfApplication || '___________________';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pad = new SignaturePad(canvas, { backgroundColor: 'rgb(255, 255, 255)' });
    sigPadRef.current = pad;

    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      pad.clear();
      setHasSig(false);
    };

    resize();
    pad.addEventListener('endStroke', () => setHasSig(!pad.isEmpty()));

    // Restore previous signature if exists
    if (state.signature.signatureData) {
      pad.fromDataURL(state.signature.signatureData);
      setHasSig(true);
    }

    return () => { pad.off(); };
  }, []);

  const clearSig = () => {
    sigPadRef.current?.clear();
    setHasSig(false);
    dispatch({ type: 'SET_NESTED', key: 'signature', value: { signatureData: null } });
  };

  const next = () => {
    const e = {};
    if (!state.signature.printedName.trim()) e.printedName = 'Printed name is required';
    if (!hasSig || sigPadRef.current?.isEmpty()) e.signature = 'Signature is required';
    if (Object.keys(e).length) { setErrs(e); return; }

    const sigData = sigPadRef.current.toDataURL('image/png');
    dispatch({ type: 'SET_NESTED', key: 'signature', value: { signatureData: sigData, signed: true } });
    onNext();
  };

  return (
    <div className="step-card">
      <h2 className="step-title">Signature Authorization</h2>
      <p className="step-subtitle">Please read the authorization below carefully before signing.</p>

      <div className="auth-text-box">
        <h4>Section 1 — General Authorization for Use of Digital Signature</h4>
        <p style={{ margin: '0 0 12px' }}>{AUTH_TEXT.section1}</p>

        <h4>Section 2 — Specific Documents included in the Driver Qualification File for <em>{company}</em></h4>
        <ul style={{ margin: '8px 0', paddingLeft: 18 }}>
          {AUTH_TEXT.documents.map((d, i) => <li key={i} style={{ marginBottom: 4 }}>{d}</li>)}
        </ul>

        <h4>Section 3</h4>
        <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{AUTH_TEXT.section3}</p>
      </div>

      <div className="field-group">
        <label className="field-label">Driver's Printed Name <span className="required">*</span></label>
        <input className={`field-input ${errs.printedName ? 'error' : ''}`}
          value={state.signature.printedName}
          onChange={e => {
            dispatch({ type: 'SET_NESTED', key: 'signature', value: { printedName: e.target.value } });
            setErrs(v => ({ ...v, printedName: '' }));
          }}
          placeholder="Type your full legal name" />
        {errs.printedName && <div className="field-error">{errs.printedName}</div>}
      </div>

      <div className="field-group">
        <label className="field-label">Signature <span className="required">*</span></label>
        <p className="field-hint" style={{ marginBottom: 8 }}>Please sign your legal signature within the box below. Take care to contain your signature within the boundaries of the box.</p>
        <div className={`signature-wrap ${errs.signature ? 'error' : ''}`} style={{ border: errs.signature ? '2px solid var(--error)' : '2px solid var(--border)' }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: 180, display: 'block' }} />
        </div>
        <div className="signature-controls">
          <button className="btn-clear" onClick={clearSig}>Clear Signature</button>
          {hasSig && <span style={{ fontSize: 14, color: 'var(--success)', paddingTop: 8 }}>✓ Signature captured</span>}
        </div>
        {errs.signature && <div className="field-error">{errs.signature}</div>}
      </div>

      <div className="wizard-nav" style={{ position: 'static', borderTop: 'none', padding: 0, marginTop: 24 }}>
        <button className="btn-back" onClick={onBack}>← Back</button>
        <button className="btn-next" onClick={next}>Next →</button>
      </div>
    </div>
  );
}
